import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/common/Seo";
import { JourneyCard } from "@/components/common/Cards";
import { SourceBadge, CategoryTag } from "@/components/common/Badges";

export default function HospitalPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setData(null); setErr(false);
    api.get(`/centres/${slug}`).then((r) => setData(r.data)).catch(() => setErr(true));
  }, [slug]);

  if (err) return <div className="max-w-7xl mx-auto px-6 py-24">Center not found.</div>;
  if (!data) return <div className="max-w-7xl mx-auto px-6 py-24 text-neutral-500">Loading…</div>;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <Seo title={`${data.hospital_name} — Transplant Story`} description={`Transplant programs and patient journeys at ${data.hospital_name}, ${data.state_name}.`} />

      <div className="flex items-start gap-5">
        <div className="w-16 h-16 shrink-0 border border-neutral-200 bg-white flex items-center justify-center font-mono text-xl text-neutral-400">
          {data.hospital_logo_url ? (
            <img src={data.hospital_logo_url} alt={data.hospital_name} className="w-full h-full object-contain p-2" loading="lazy" />
          ) : (
            data.hospital_name.charAt(0)
          )}
        </div>
        <div>
          <h1 className="text-3xl sm:text-4xl font-light tracking-tight text-ink leading-tight">{data.hospital_name}</h1>
          <div className="text-sm font-mono text-neutral-500 mt-2">
            {data.city && data.city.toLowerCase() !== (data.state_name || "").toLowerCase() ? `${data.city}, ${data.state_name}` : data.state_name}
          </div>
        </div>
      </div>

      {/* OFFICIAL INFORMATION */}
      <section className="mt-12 bg-neutral-50/70 border border-neutral-300 p-6">
        <CategoryTag kind="official" />
        <h2 className="text-lg font-medium text-ink mt-4">Transplant programs</h2>
        <div className="flex flex-wrap gap-2 mt-3">
          {data.organ_programs.map((o) => (
            <span key={o.slug} className="text-sm font-mono bg-white border border-neutral-200 text-neutral-700 px-3 py-1 rounded-sm">{o.name}</span>
          ))}
        </div>
        <div className="mt-6 grid sm:grid-cols-2 gap-4 text-sm">
          <Info label="Status" value={data.notto_status} />
          {data.official_website && (
            <div>
              <div className="text-[11px] font-mono uppercase tracking-widest text-neutral-400 mb-1">Official website</div>
              <a href={data.official_website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-rust hover:text-rust-hover break-all">
                Visit <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          )}
        </div>
        <div className="mt-5"><SourceBadge source={data.official_source.source} updated={data.official_source.updated} /></div>
      </section>

      {/* PLATFORM NOTE */}
      <p className="text-xs text-neutral-500 mt-4 leading-relaxed max-w-2xl">
        A logo, where shown, is used only for identification. It does not imply endorsement by the hospital.
        This platform does not rank, score or recommend hospitals.
      </p>

      {/* PATIENT JOURNEYS */}
      <section className="mt-14">
        <div className="flex items-center gap-3 mb-2"><CategoryTag kind="patient" /></div>
        <h2 className="text-2xl font-normal tracking-tight text-ink mt-3">Patient journeys</h2>
        <div className="flex gap-8 mt-4 font-mono text-sm text-neutral-600">
          <span>{data.patient_journey_count} shared</span>
          <span>{data.voice_journey_count} voice</span>
        </div>

        {data.journeys.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
            {data.journeys.map((j) => <JourneyCard key={j.id} journey={j} />)}
          </div>
        ) : (
          <div className="border border-dashed border-neutral-300 p-10 mt-8 text-center">
            <p className="text-neutral-700">No journeys shared for this center yet.</p>
            <Link to="/share" className="inline-block mt-4 text-sm font-medium text-rust hover:text-rust-hover">Share your journey →</Link>
          </div>
        )}
      </section>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[11px] font-mono uppercase tracking-widest text-neutral-400 mb-1">{label}</div>
      <div className="text-ink">{value || "—"}</div>
    </div>
  );
}
